/**
 * @fileoverview Security Settings Panel - User-facing security configuration
 *
 * Provides UI for managing security-related preferences including:
 * - Unlock duration settings
 * - Device management
 * - Session management
 *
 * @module components/SecuritySettingsPanel
 */

import { useState } from 'react';
import { Settings, Lock, LogOut } from 'lucide-react';
import { UnlockDurationSettings } from './UnlockDurationSettings';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('SecuritySettingsPanel');

interface SecuritySettingsPanelProps {
  onClose?: () => void;
  showCloseButton?: boolean;
}

type SettingsTab = 'unlock-duration' | 'sessions' | 'devices';

export function SecuritySettingsPanel({
  onClose,
  showCloseButton = true,
}: SecuritySettingsPanelProps) {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('unlock-duration');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (
      !confirm(
        'Are you sure you want to logout? You will need to unlock again on next visit.',
      )
    ) {
      return;
    }

    setIsLoggingOut(true);
    try {
      await signOut();
      logger.info('User logged out from security settings');
      onClose?.();
    } catch (error) {
      logger.error('Failed to logout', error as Error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className='bg-card rounded-lg shadow-lg max-w-2xl w-full'>
      {/* Header */}
      <div className='flex items-center justify-between p-6 border-b border-border'>
        <div className='flex items-center gap-3'>
          <Lock className='w-6 h-6 text-blue-600' />
          <h2 className='text-2xl font-bold text-foreground'>
            Security Settings
          </h2>
        </div>
        {showCloseButton && (
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            aria-label='Close'
          >
            ✕
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className='flex border-b border-border'>
        <button
          onClick={() => setActiveTab('unlock-duration')}
          className={`flex-1 py-4 px-6 font-medium border-b-2 transition-colors ${
            activeTab === 'unlock-duration'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-gray-900 dark:hover:text-gray-300'
          }`}
        >
          🔓 Unlock Duration
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`flex-1 py-4 px-6 font-medium border-b-2 transition-colors ${
            activeTab === 'sessions'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-gray-900 dark:hover:text-gray-300'
          }`}
        >
          📊 Sessions
        </button>
        <button
          onClick={() => setActiveTab('devices')}
          className={`flex-1 py-4 px-6 font-medium border-b-2 transition-colors ${
            activeTab === 'devices'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-gray-900 dark:hover:text-gray-300'
          }`}
        >
          💻 Devices
        </button>
      </div>

      {/* Content */}
      <div className='p-6'>
        {activeTab === 'unlock-duration' && (
          <div>
            <UnlockDurationSettings compact={true} />
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className='space-y-4'>
            <div className='bg-info/10 dark:bg-info/5 border border-info/30 rounded-lg p-4'>
              <p className='text-sm text-blue-700 dark:text-blue-300'>
                ℹ️ Session management features coming soon. Currently, sessions
                are managed through your unlock duration preference.
              </p>
            </div>

            <div className='space-y-3'>
              <h3 className='font-semibold text-foreground'>Current Session</h3>
              <div className='bg-muted rounded p-4 space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Status</span>
                  <span className='font-medium text-success'>✓ Active</span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>User</span>
                  <span className='font-medium text-foreground'>
                    {user.email}
                  </span>
                </div>
              </div>
            </div>

            <div className='mt-6'>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className='w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors'
              >
                <LogOut className='w-4 h-4' />
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
              <p className='text-xs text-muted-foreground mt-2 text-center'>
                You will need to unlock again on your next visit
              </p>
            </div>
          </div>
        )}

        {activeTab === 'devices' && (
          <div className='space-y-4'>
            <div className='bg-info/10 dark:bg-info/5 border border-info/30 rounded-lg p-4'>
              <p className='text-sm text-blue-700 dark:text-blue-300'>
                ℹ️ Device management features coming soon. View and manage your
                registered devices here.
              </p>
            </div>

            <div className='space-y-3'>
              <h3 className='font-semibold text-foreground'>Current Device</h3>
              <div className='bg-muted rounded p-4 space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Device Type</span>
                  <span className='font-medium text-foreground'>
                    {navigator.userAgent.includes('Mac') ? 'macOS' : 'Unknown'}
                  </span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-muted-foreground'>Browser</span>
                  <span className='font-medium text-foreground'>
                    {navigator.userAgent.includes('Chrome')
                      ? 'Chrome'
                      : navigator.userAgent.includes('Safari')
                        ? 'Safari'
                        : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
